import React, { useState, useRef, useEffect } from "react";
import {
  createPTag,
  deletePTag,
  getPTagAll,
  updatePTag,
  softDeletePTag,
  getLanguages,
} from "../../Services/ProductTagService";
import { getTagAll } from "../../Services/TagService";
import { getCountryAll } from "../../Services/CountryService";
import { useForm, Controller } from "react-hook-form";
import Swal from "sweetalert2";
import MTable from "../../Components/MTable/MTable";
import { InputSwitch } from "primereact/inputswitch";
import Overlay from "../../Components/Overlay/Overlay";
import { handlerFormError, uuid } from "../../Utils/Utils";
import { toastError, toastSuccess } from "../../Components/Toasts/Toast";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import EditButton from "../../Components/Button/EditButton";
import DeleteButton from "../../Components/Button/DeleteButton";

let processingId = -1;

const ProductTagPage = () => {
  let { userInfo } = useSelector((state) => state.auth);
  const [showAll, setShowAll] = useState(false);
  const mTable = useRef();
  const {
    register,
    setValue,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      master_tag_id: "",
      title: "",
      description: "",
      country_code: "",
      language_code: "",
      is_active: true,
    },
  });

  const [state, setState] = useState({
    tags: [],
    countries: [],
    languages: [],
    showForm: false,
    productTag: {
      master_tag_id: "",
      title: "",
      description: "",
      country_code: "",
      language_code: "",
      is_active: true,
    },
    processing: false,
  });

  const startProcessing = () => {
    processingId = setTimeout(() => {
      setState({ ...state, processing: true });
    }, 150);
  };

  const stopProcessing = () => {
    clearTimeout(processingId);
    setState({ ...state, processing: false });
  };

  const columns = [
    { id: uuid(), title: "Title", field: "title", sortable: true },
    { id: uuid(), title: "Description", field: "description", sortable: true },
    {
      id: uuid(),
      title: "Country",
      field: "country_name",
      sortable: true,
      render: (item) => {
        return item.country_name ? (
          item.country_name
        ) : (
          <p className="text-red">Not Found</p>
        );
      },
    },
    { id: uuid(), title: "Language", field: "language_name", sortable: true },
    {
      id: uuid(),
      title: "Active",
      field: "is_active",
      render: (item) => {
        if (item.is_active === true) {
          return <span className="badge badge-success">Active</span>;
        } else {
          return <span className="badge badge-secondary">Inactive</span>;
        }
      },
    },
    {
      id: uuid(),
      title: "",
      sortable: true,
      style: { width: 200 },
      render: (item) => {
        return (
          <div>
            {userInfo.access.product_tag.can_update && (
              <EditButton title="Edit" onClick={onEdit(item)} />
            )}
            {userInfo.access.product_tag.can_delete && (
              <DeleteButton title="Remove" onClick={onRemove(item)} />
            )}
          </div>
        );
      },
    },
  ];

  const onActiveChange = (item) => (e) => {
    if (!userInfo.access.master.can_update) {
      toastError("You dont have permission for this action");
      return;
    }

    const status = e.target.value ? "Activate" : "Inactive";
    softDeletePTag(item.id, { is_active: e.target.value })
      .then((res) => {
        toastSuccess(`${status} ${item.title} Success.`);
        mTable.current.refresh();
      })
      .catch(({ response: { err } }) => {
        toastError(`${status} Failed.`);
      });
  };

  const removeData = (id) => {
    deletePTag(
      id,
      (res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Delete data success",
            text: "Data has been deleted!",
          }).then((res) => {
            mTable.current.refresh();
          });
        }
      },
      ({ response: { data } }) => {
        Swal.fire({
          icon: "error",
          title: "Delete data fail",
          text: data.message,
        });
      }
    );
  };

  const onAddData = () => {
    if (!userInfo.access.product_tag.can_create) {
      return;
    }
    onReset();
    setState({ ...state, showForm: true });
  };

  const onEdit = (item) => () => {
    if (!userInfo.access.product_tag.can_update) {
      return;
    }
    setValue("master_tag_id", item.master_tag_id);
    setValue("tag", item.tag);
    setValue("description", item.description);
    setValue("slug", item.slug);
    setValue("country_name", item.country_name);
    setValue("country_code", item.country_code);
    setValue("language_code", item.language_code);
    setValue("language_name", item.language_name);

    setValue("is_active", item.is_active);
    reset(item);
    setState({ ...state, productTag: item, showForm: true });
  };

  const onRemove = (item) => () => {
    if (!userInfo.access.product_tag.can_delete) {
      toastError("You dont have permission for this action");
      return;
    }

    Swal.fire({
      icon: "question",
      title: "Are you sure?",
      text: "Deleted data can not be restored!",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        removeData(item.id);
      }
    });
  };

  const onSubmit = (data) => {
    startProcessing();
    const response = productTag.id
      ? updatePTag(productTag.id, data)
      : createPTag(data);
    response
      .then((res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Save data success",
            text: "Data has been saved!",
          }).then((r) => {
            mTable.current.refresh();
            productTag.id ? onCloseForm() : onReset();
          });
        }
      })
      .catch(({ response: { data } }) => {
        handlerFormError(data);
      })
      .finally((_) => stopProcessing());
  };

  const onReset = () => {
    reset({
      master_tag_id: "",
      title: "",
      description: "",
      country_code: "",
      language_code: "",
      is_active: true,
    });
    setState({ ...state, productTag: {} });
  };

  const onCloseForm = () => {
    reset({
      master_tag_id: "",
      title: "",
      description: "",
      country_code: "",
      language_code: "",
      is_active: true,
    });
    setState({ ...state, productTag: {}, showForm: false });
  };

  const { tags, countries, languages, productTag, processing, showForm } =
    state;

  const showAllFunc = () => {
    setShowAll(!showAll);
    mTable.current.refresh();
  };

  const propsTable = {
    columns,
    showAllState: showAll,
    showAllFunc,
    getData: (params) => {
      if (showAll === false) {
        if (params.filterAnd) params.filterAnd += "," + `is_active:true`;
        else params.filterAnd = "is_active:true";
      }
      return getPTagAll({ ...params });
    },
    enableFilter: true,
    showAddButton: !userInfo.access.product_tag.can_create ? false : true,
  };

  useEffect(() => {
    const reqTag = getTagAll({ orders: "tag:asc", perpage: 1000 }).then(
      (res) => res.data.data
    );
    const reqCountry = getCountryAll({
      orders: "country_name:asc",
      perpage: 1000,
    }).then((res) => res.data.data);
    const reqLanguage = getLanguages({ perpage: 1000 }).then(
      (res) => res.data.data
    );

    Promise.all([reqTag, reqCountry, reqLanguage]).then((results) => {
      const [_tags, _countries, _languages] = results;
      setState({
        ...state,
        tags: _tags,
        countries: _countries,
        languages: _languages,
      });
    });
  }, []);

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h4
                style={{
                  marginTop: "0.5rem",
                  marginLeft: "0.2rem",
                  fontWeight: "400",
                }}
              >
                {showForm ? "Product Tag Form" : "Product Tag Management"}
              </h4>
              <h6
                style={{
                  marginLeft: "0.2rem",
                  fontWeight: "200",
                  letterSpacing: "1.2px",
                }}
              >
                {showForm
                  ? "Add/Edit a Product Tag"
                  : "Database of Product Tags"}
              </h6>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <NavLink to="/home">Home</NavLink>
                </li>
                <li className="breadcrumb-item active">Product Tag</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <Overlay display={processing} />
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-tags mr-1" />
                    {showForm ? "Form Product Tag" : "List Product Tag"}
                  </h3>
                </div>

                <form name="form-detail" onSubmit={handleSubmit(onSubmit)}>
                  <div className="card-body">
                    <div
                      className={`${
                        !showForm
                          ? "card__side--front-show"
                          : "card__side--front"
                      }`}
                    >
                      <MTable
                        ref={mTable}
                        {...propsTable}
                        onAddData={onAddData}
                        showIndex={true}
                      />
                    </div>

                    <div
                      className={`${
                        showForm ? "card__side--back-show" : "card__side--back"
                      }`}
                    >
                      <div className="row">
                        <div className="col-md-4">
                          {
                            //productTag.language_code == '' &&
                            <div className="form-group">
                              <label>Base Tag</label>
                              <select
                                id="master_tag_id"
                                className="form-control"
                                {...register("master_tag_id", {
                                  required: {
                                    value: true,
                                    message: "Base Tag is required!",
                                  },
                                })}
                              >
                                <option value={""}>Select Base Tag</option>
                                {tags.map((item, i) => (
                                  <option key={i} value={item.id}>
                                    {item.tag}
                                  </option>
                                ))}
                              </select>
                              {errors.master_tag_id && (
                                <span
                                  className="text-danger"
                                  style={{ fontSize: 14 }}
                                >
                                  {errors.master_tag_id.message}
                                </span>
                              )}
                            </div>
                          }

                          <div className="form-group">
                            <label>Country</label>
                            <select
                              id="country_code"
                              className="form-control"
                              {...register("country_code", {
                                required: {
                                  value: true,
                                  message: "Country is required!",
                                },
                              })}
                            >
                              <option value={""}>Select Country</option>
                              {countries.map((item, i) => (
                                <option key={i} value={item.country_code}>
                                  {item.country_name}
                                </option>
                              ))}
                            </select>
                            {errors.country_code && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.country_code.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label>Language</label>
                            <select
                              id="language_code"
                              className="form-control"
                              {...register("language_code", {
                                required: {
                                  value: true,
                                  message: "Language is required!",
                                },
                              })}
                            >
                              <option value={""}>Select Language</option>
                              {languages.map((item, i) => (
                                <option key={i} value={item.language_code}>
                                  {item.language_name}
                                </option>
                              ))}
                            </select>
                            {errors.language_code && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.language_code.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="title">Title</label>
                            <input
                              id="title"
                              {...register("title", {
                                required: {
                                  value: true,
                                  message: "Title is required!",
                                },
                              })}
                              placeholder="Title"
                              className="form-control"
                            />
                            {errors.title && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.title.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="description">Description</label>
                            <input
                              id="description"
                              {...register("description", {
                                required: {
                                  value: true,
                                  message: "Description is required!",
                                },
                              })}
                              placeholder="Description"
                              className="form-control"
                            />
                            {errors.description && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.description.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="sub-category-name">Active</label>
                            <div>
                              <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => {
                                  return (
                                    <InputSwitch
                                      {...field}
                                      checked={field.value}
                                    />
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {showForm && (
                    <div className="card-footer delay">
                      <div className="row">
                        <div className="col-md-12">
                          <div
                            className="d-flex"
                            style={{ justifyContent: "flex-end" }}
                          >
                            <button
                              type="button"
                              className="btn btn-default mr-3"
                              style={{ width: 100 }}
                              onClick={onCloseForm}
                            >
                              <i className="fa fa-times" /> Close
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger mr-3"
                              style={{ width: 100 }}
                              onClick={onReset}
                            >
                              <i className="fa fa-sync" /> Clear
                            </button>
                            <button
                              type="submit"
                              className="btn btn-dark"
                              style={{ width: 100 }}
                              disabled={
                                control._formValues.id
                                  ? !userInfo.access.product_tag.can_update
                                  : !userInfo.access.product_tag.can_create
                              }
                            >
                              <i className="fa fa-save" /> Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductTagPage;
